// src/prototype/pages/EAExecuteHubPage.tsx
// ============================================================
// EXECUTE HUB  (/execute/:initiativeId/:tab  where tab ∈ summary | approval | vendors | execution)
//
// Oct 21 redesign (v3). Mirrors the Evaluate use-case hub: one route per use case with the tab
// in the URL, a persistent header (identity, approval state, owner, KPI row), prev/next across
// the Execute portfolio that preserves the tab, and Pro-only gating through useEntitlements.
//
// Replaces the single-page Initiative Command Center (still in the tree as
// EAInitiativeCommandCenterPage for reference; v2 is tagged v2-drilldown-redesign).
// /execute/:initiativeId (no tab) redirects to the Summary tab, so existing links keep working.
// ============================================================

import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  ArrowLeft, ChevronLeft, ChevronRight, LayoutDashboard, Scale, Store, ClipboardList,
  TrendingUp, Clock, DollarSign, BarChart3, Calendar, Percent, FileText,
} from 'lucide-react';

import { APP_HEADER_HEIGHT_PX } from '../../components/header/headerMetrics';
import { EAJourneyBarFrame } from '../components/EAJourneyBarFrame';
import { StatTile } from '../components/StatTile';
import { ExecStatusPill } from '../components/ExecStatusPill';
import { VerdictPill } from '../components/VerdictPill';
import { AsOfChip } from '../components/hub/AsOfChip';
import { ExecSummaryTab } from '../components/execute/ExecSummaryTab';
import { ExecApprovalTab } from '../components/execute/ExecApprovalTab';
import { resolveSteps, PERSONAS, actorLabel, type ResolvedStep } from '../data/execute/approvalWorkflow';
import { useActingAs } from '../hooks/useActingAs';
import { ExecVendorLandscapeTab } from '../components/execute/ExecVendorLandscapeTab';
import { ExecPmoHandoffTab } from '../components/execute/ExecPmoHandoffTab';

import { MOCK_OPPORTUNITIES } from '../data/mockOpportunities';
import { getDecisionBrief, type DecisionBrief } from '../data/decisionBrief';
import { getInitiativeRecord, type InitiativeRecord } from '../data/executionInitiatives';
import { getScores, getRequirements, SCORES_AS_OF, SCORING_RUN } from '../data/adapters';
import { DEMO_CLIENT } from '../config/demoClient';
import { VERDICTS } from '../theme/decisionTokens';
import { useEA } from '../contexts/EAContext';
import { useEntitlements } from '../hooks/useEntitlements';
import useExecutePortfolio from '../hooks/useExecutePortfolio';
import useDemoUploads from '../hooks/useDemoUploads';
import { useExecutionRecordState, blockerCleared } from '../hooks/useExecutionRecordState';
import { useExecuteHubState, type Signoff } from '../hooks/useExecuteHubState';
import { commitmentsByOpportunity } from '../utils/initiativesImport';
import { getOpportunityRank } from '../utils/opportunityRanking';
import { roiForUseCase } from '../roi/useCaseRoi';
import { formatUsd, formatUsdRange } from '../roi/roiEngine';

export type ExecTab = 'summary' | 'approval' | 'vendors' | 'execution';
const TABS: { key: ExecTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'summary', label: 'Summary', icon: LayoutDashboard },
  { key: 'approval', label: 'Approval', icon: Scale },
  { key: 'vendors', label: 'Vendor Landscape', icon: Store },
  { key: 'execution', label: 'Use Case Execution', icon: ClipboardList },
];
const isTab = (t: string | undefined): t is ExecTab => TABS.some((x) => x.key === t);

/** /execute/:initiativeId → /execute/:initiativeId/summary (keeps old links working). */
export function ExecuteHubRedirect() {
  const { initiativeId } = useParams<{ initiativeId: string }>();
  const location = useLocation();
  return <Navigate to={`/execute/${initiativeId}/summary${location.search}`} replace state={location.state} />;
}

export default function EAExecuteHubPage() {
  const { initiativeId, tab } = useParams<{ initiativeId: string; tab: string }>();
  const navigate = useNavigate();
  const { can } = useEntitlements();
  const { categoryWeights } = useEA();

  // Execute is Pro-only.
  useEffect(() => {
    if (!can.execute) navigate('/upgrade', { replace: true });
  }, [can.execute, navigate]);

  const opportunity = useMemo(() => MOCK_OPPORTUNITIES.find((o) => o.id === initiativeId), [initiativeId]);
  const rank = getOpportunityRank(initiativeId || '', categoryWeights);
  const brief = useMemo(() => (opportunity ? getDecisionBrief(opportunity, rank) : null), [opportunity, rank]);
  const record = useMemo(() => (brief ? getInitiativeRecord(brief.id, brief) : null), [brief]);

  if (!isTab(tab)) return <Navigate to={`/execute/${initiativeId}/summary`} replace />;

  if (!brief || !record) {
    return (
      <div className="min-h-screen bg-[#F5F7FA]">
        <EAJourneyBarFrame compact />
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="bg-white rounded-xl border border-[#e5e7eb] px-6 py-14 text-center">
            <p className="text-sm font-bold text-[#18365E]">Initiative not found</p>
            <p className="text-xs text-[#717182] mt-1">No initiative matches <span className="font-mono">{initiativeId}</span>.</p>
            <button onClick={() => navigate('/activate')} className="mt-5 bg-[#FF6E4C] text-white text-xs font-semibold px-4 py-2 rounded-lg inline-flex items-center gap-1.5 hover:bg-[#e85f3f] transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" />Execution portfolio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Hooks that need a resolved record live in the body component, so hook order never
  // depends on the not-found branch above.
  return <ExecuteHubBody key={brief.id} brief={brief} record={record} tab={tab} />;
}

function ExecuteHubBody({ brief, record, tab }: { brief: DecisionBrief; record: InitiativeRecord; tab: ExecTab }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { portfolio } = useExecutePortfolio();
  const { initiatives } = useDemoUploads();

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, [brief.id]);

  const live = useExecutionRecordState(record.id, record.milestones, record.checklist);
  const hub = useExecuteHubState(brief.id);
  const { persona, setPersonaId } = useActingAs();
  const actor = actorLabel(persona);
  const scores = getScores(brief);
  const roi = roiForUseCase(brief.id);
  const base = roi.scenarios.base;
  const commitment = commitmentsByOpportunity(initiatives).get(brief.id);
  const requirements = getRequirements(brief);

  const milestones = live.milestones.map((m) =>
    m.blocker && blockerCleared(m.blocker, live.milestones) ? { ...m, blocker: undefined } : m,
  );

  const steps = resolveSteps(record, hub.state.steps);
  const blockedStep = steps.find((s) => s.status === 'blocked');
  const currentStep = steps.find((s) => s.status === 'current');
  const overdueStep = steps.find((s) => s.overdue);
  const approvalState = blockedStep
    ? `${blockedStep.decision?.decision === 'deferred' ? 'Deferred' : 'Declined'} at ${blockedStep.label}`
    : currentStep ? `Awaiting: ${currentStep.label}` : 'Approved';
  const firstOpenChecklist = live.checklist.find((c) => !c.done);
  const nextOpenItem = currentStep?.items.find((x) => !x.done);
  // Where the next action lives decides where its link goes.
  const nextAction: { text: string; tab: 'approval' | 'execution' } = blockedStep
    ? { text: `Address the ${blockedStep.decision?.decision === 'deferred' ? 'deferral' : 'decline'} at ${blockedStep.label}`, tab: 'approval' }
    : overdueStep ? { text: `Overdue: ${overdueStep.label}`, tab: 'approval' }
    : currentStep && nextOpenItem ? { text: `${currentStep.label}: ${nextOpenItem.label}`, tab: 'approval' }
    : currentStep ? { text: `Decision needed: ${currentStep.label}`, tab: 'approval' }
    : firstOpenChecklist ? { text: `Activation checklist: ${firstOpenChecklist.label}`, tab: 'execution' }
    : { text: 'Hand off to the PMO', tab: 'execution' };

  const ranges = {
    annual: formatUsdRange(base.annualValueLow, base.annualValueHigh),
    fullRange: formatUsdRange(roi.annualValueRangeLow, roi.annualValueRangeHigh),
    netValue3Yr: formatUsd(base.netValue3Yr),
    payback: `${base.paybackMonths} months`,
    conservativePayback: `${roi.scenarios.conservative.paybackMonths} months`,
  };

  // Paging across the Execute portfolio, keeping the tab.
  const idx = portfolio.indexOf(brief.id);
  const prevId = idx > 0 ? portfolio[idx - 1] : null;
  const nextId = idx >= 0 && idx < portfolio.length - 1 ? portfolio[idx + 1] : null;
  const page = (id: string | null) => { if (id) navigate(`/execute/${id}/${tab}`); };
  const goTab = (t: ExecTab) => navigate(`/execute/${brief.id}/${t}`, { replace: true, state: location.state });

  const openBrief = () => navigate(`/opportunity/${brief.id}/brief`, { state: { from: 'activate', returnTo: location.pathname } });
  const openRoiModel = () => navigate(`/opportunity/${brief.id}/roi-model`);

  // Record a decision; capture the decision snapshot when the chain resolves (final approval,
  // or any defer/decline).
  const onDecide = (step: ResolvedStep, s: Signoff) => {
    hub.decide(step.index, step.label, s, actor);
    const isLast = step.index === record.approval.length - 1;
    if (s.decision !== 'approved' || isLast) {
      const approvers = [...steps.filter((x) => x.decision && x.index !== step.index).map((x) => x.decision!), s]
        .map((x) => `${x.approver} (${x.role})`);
      hub.setSnapshot({
        decision: s.decision,
        date: s.date,
        approvers,
        compositeScore: scores.total,
        verdict: VERDICTS[brief.verdict].label,
        scoringRun: SCORING_RUN,
        scoresAsOf: SCORES_AS_OF,
        annualValueRange: ranges.fullRange,
        netValue3Yr: ranges.netValue3Yr,
        payback: ranges.payback,
        keyAssumptions: record.cfo.assumptions.slice(0, 5).map((a) => `${a.label}: ${a.value}`),
      });
    }
  };

  const setMilestoneStatus = (title: string, status: Parameters<typeof live.setMilestoneStatus>[1], pick?: number) => {
    live.setMilestoneStatus(title, status, pick);
    hub.logMilestone(title, `Status set to ${status}${pick ? ` (${pick}%)` : ''}`, actor);
  };
  const toggleChecklist = (label: string) => {
    const was = live.checklist.find((c) => c.label === label)?.done;
    live.toggleChecklistItem(label);
    hub.logMilestone('Activation checklist', `${was ? 'Unchecked' : 'Completed'}: ${label}`, actor);
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <EAJourneyBarFrame compact />

      {/* Sticky utility bar */}
      <div style={{ top: APP_HEADER_HEIGHT_PX }} className="sticky z-10 bg-white/95 backdrop-blur border-b border-[#e5e7eb] px-6 py-2.5">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate('/activate')} className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors shrink-0">
            <ArrowLeft className="w-3.5 h-3.5" />Execution portfolio
          </button>
          <span className="text-[#e5e7eb]" aria-hidden="true">|</span>
          <span className="text-xs font-bold text-[#18365E] truncate">{brief.title}</span>
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-[#717182] shrink-0">
            Acting as
            <select value={persona.id} onChange={(e) => setPersonaId(e.target.value)} aria-label="Acting as"
              className="text-[11px] font-semibold text-[#18365E] border border-[#e5e7eb] rounded-md px-1.5 py-1 bg-white">
              {PERSONAS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </label>
          <div className="flex items-center gap-1 shrink-0">
            {idx >= 0 && <span className="text-[10px] text-[#717182] mr-1.5 hidden sm:block">Initiative {idx + 1} of {portfolio.length}</span>}
            <button onClick={() => page(prevId)} disabled={!prevId} aria-label="Previous initiative (keeps this tab)" className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={() => page(nextId)} disabled={!nextId} aria-label="Next initiative (keeps this tab)" className="w-7 h-7 rounded-lg border border-[#e5e7eb] flex items-center justify-center text-[#18365E] disabled:opacity-30 hover:border-[#18365E] transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* ── PERSISTENT HEADER ── */}
        <div className="bg-white rounded-t-xl border border-b-0 border-[#e5e7eb] p-6 pb-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-[10px] font-semibold text-dvaNavy uppercase tracking-wide">{brief.category}</span>
                <ExecStatusPill status={record.status} />
                {commitment?.committed && (
                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#18365E] text-white">{DEMO_CLIENT.name} Committed</span>
                )}
              </div>
              <h1 className="text-2xl font-bold text-[#18365E] leading-tight">{brief.title}</h1>
              <p className="text-xs text-[#4b5563] mt-1.5">
                <span className="text-[#717182]">Owner</span> <b className="text-[#18365E]">{record.owner}</b>
                <span className="mx-2 text-[#e5e7eb]">|</span>
                <span className="text-[#717182]">Approval</span> <b className="text-[#18365E]">{approvalState}</b>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button onClick={openBrief} className="flex items-center gap-1.5 text-xs font-semibold text-[#18365E] border border-[#e5e7eb] rounded-lg px-3 py-2 hover:border-[#18365E] transition-colors">
                <FileText className="w-3.5 h-3.5" />Evaluation brief
              </button>
              <button onClick={openRoiModel} className="bg-[#18365E] text-white text-xs font-semibold px-4 py-2 rounded-lg flex items-center gap-1.5 hover:bg-[#122a4a] transition-colors">
                <TrendingUp className="w-3.5 h-3.5" />ROI Model
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 min-w-0">
            <span className="shrink-0 whitespace-nowrap"><VerdictPill verdict={brief.verdict} /></span>
            <p className="text-sm text-[#4b5563] truncate">{record.objective}</p>
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
            <StatTile variant="dominant" label="Composite Score" value={`${scores.total} / 100`} icon={<BarChart3 className="w-5 h-5" />} />
            <StatTile label="Net 3-Year Value" value={ranges.netValue3Yr} icon={<DollarSign className="w-4 h-4" />} />
            <StatTile label="Payback" value={ranges.payback} icon={<Clock className="w-4 h-4" />} />
            <StatTile wrap label="Annual Value" value={ranges.annual} icon={<TrendingUp className="w-4 h-4" />} />
            <StatTile label="Milestones" value={`${live.milestonesComplete} of ${live.milestones.length}`} icon={<Calendar className="w-4 h-4" />} />
            <StatTile label="Percent Complete" value={commitment?.percentComplete != null ? `${commitment.percentComplete}%` : 'Not reported'} icon={<Percent className="w-4 h-4" />} />
          </div>
          <div className="mt-2.5 flex items-center justify-between gap-3">
            <span className="text-[10px] text-[#717182]">Financial figures: Base Case scenario</span>
            <AsOfChip />
          </div>
        </div>

        {/* ── TABS + BODY ── */}
        <div className="border border-t-0 border-[#e5e7eb] rounded-b-xl bg-[#F5F7FA]">
          <div className="flex items-center gap-1 border-b border-[#e5e7eb] bg-white px-3 overflow-x-auto" role="tablist">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={active}
                  onClick={() => goTab(t.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-3 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${active ? 'border-[#FF6E4C] text-[#FF6E4C]' : 'border-transparent text-[#717182] hover:text-[#18365E]'}`}
                >
                  <Icon className="w-3.5 h-3.5" />{t.label}
                </button>
              );
            })}
          </div>

          <div className="p-5">
            {tab === 'summary' && (
              <ExecSummaryTab
                brief={brief} record={record} scores={scores} milestones={milestones}
                approvalState={approvalState} nextAction={nextAction}
                percentComplete={commitment?.percentComplete ?? null} committed={Boolean(commitment?.committed)}
                ranges={ranges} onGoTab={goTab} onOpenBrief={openBrief} activity={hub.state.activity}
              />
            )}
            {tab === 'approval' && (
              <ExecApprovalTab
                key={brief.id}
                useCaseId={brief.id} record={record} steps={steps}
                notes={hub.state.notes} activity={hub.state.activity} snapshot={hub.state.snapshot}
                persona={persona} hub={hub} onDecide={onDecide} onOpenRoiModel={openRoiModel}
              />
            )}
            {tab === 'vendors' && <ExecVendorLandscapeTab useCaseId={brief.id} clientName={DEMO_CLIENT.name} useCaseTitle={brief.title} />}
            {tab === 'execution' && (
              <ExecPmoHandoffTab
                brief={brief} record={record} clientName={DEMO_CLIENT.name} scoresAsOf={SCORES_AS_OF}
                milestones={milestones} checklist={live.checklist} requirements={requirements}
                handoff={hub.state.handoff} onHandoff={hub.setHandoff}
                onSetMilestoneStatus={setMilestoneStatus} onToggleChecklist={toggleChecklist}
                milestoneNotes={hub.state.notes} onAddMilestoneNote={(title, text) => hub.addNote({ kind: 'milestone', title }, text, actor)}
                updatedLabelFor={(title) => { const o = live.overrides[title]; return o ? `Updated just now · ${o.updatedBy}` : undefined; }}
                onGoApproval={() => goTab('approval')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
